import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { db, collection, onSnapshot, query, orderBy, handleFirestoreError, OperationType, addDoc, updateDoc, doc, deleteDoc, Timestamp, where, createNotification, getDocs } from '../firebase';
import { Task, TaskStatus, Client, UserProfile, Branch, ReportType, ReportStatus } from '../types';
import { useAuth } from '../App';
import FeedbackModal from '../components/FeedbackModal';
import { FilterBar, FilterOption } from '../components/FilterBar';
import { 
  Plus, 
  Search, 
  Wrench, 
  Clock, 
  CheckCircle2, 
  AlertCircle,
  Building2,
  Trash2,
  Edit2,
  X,
  Filter,
  Star,
  ChevronDown,
  ChevronUp,
  Printer,
  Phone,
  Mail,
  User as UserIcon,
  Calendar as CalendarIcon,
  LayoutGrid,
  ChevronLeft,
  ChevronRight,
  Loader2,
  FileText,
  Send,
  Paperclip,
  Image,
  Upload
} from 'lucide-react';
import { 
  format, 
  startOfMonth, 
  endOfMonth, 
  startOfWeek, 
  endOfWeek, 
  eachDayOfInterval, 
  isSameMonth, 
  isSameDay, 
  addMonths, 
  subMonths,
  isToday
} from 'date-fns';
import { motion, AnimatePresence } from 'motion/react';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { toast } from 'sonner';
import ConfirmationModal from '../components/ConfirmationModal';

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

const TasksPage: React.FC = () => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [technicians, setTechnicians] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<TaskStatus | 'all'>('all');
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest'>('newest');
  const [searchParams] = useSearchParams();
  const clientIdParam = searchParams.get('clientId');
  const [filterClientId, setFilterClientId] = useState<string | 'all'>(clientIdParam || 'all');
  const [filterDateStart, setFilterDateStart] = useState<string>('');
  const [filterDateEnd, setFilterDateEnd] = useState<string>('');
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('list');
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const filterOptions: FilterOption[] = [
    {
      key: 'status',
      label: 'All Status',
      type: 'select',
      options: [
        { value: 'pending', label: 'Pending' },
        { value: 'in-progress', label: 'In Progress' },
        { value: 'completed', label: 'Completed' },
      ]
    },
    {
      key: 'clientId',
      label: 'All Clients',
      type: 'select',
      options: clients.map(c => ({ value: c.id, label: c.name }))
    },
    {
      key: 'dueDate',
      label: 'Due Date Range',
      type: 'dateRange'
    },
    {
      key: 'sort',
      label: 'Sort By',
      type: 'select',
      options: [
        { value: 'newest', label: 'Newest First' },
        { value: 'oldest', label: 'Oldest First' },
      ]
    }
  ];

  const handleFilterChange = (key: string, value: any) => {
    if (key === 'status') setFilterStatus(value);
    if (key === 'clientId') setFilterClientId(value);
    if (key === 'dueDateStart') setFilterDateStart(value);
    if (key === 'dueDateEnd') setFilterDateEnd(value);
    if (key === 'sort') setSortOrder(value);
  };
// ...
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [selectedTaskForReport, setSelectedTaskForReport] = useState<Task | null>(null);
  const [showConfirmCreate, setShowConfirmCreate] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [selectedTaskForFeedback, setSelectedTaskForFeedback] = useState<Task | null>(null);
  const [expandedTaskId, setExpandedTaskId] = useState<string | null>(null);
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
  });
  const [clientBranches, setClientBranches] = useState<Branch[]>([]);
  const [isBranchLoading, setIsBranchLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { profile, isAdmin, isManager, isTechnician } = useAuth();

  // Attachment upload and delete handlers
  const handleAttachmentUpload = async (taskId: string, e: React.ChangeEvent<HTMLInputElement>, currentAttachments: any[] = []) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate size (max 800KB for firestore storage safety)
    if (file.size > 800000) {
      toast.error('File is too large. Please select an image or PDF under 800KB to stay within secure limits.');
      return;
    }

    const reader = new FileReader();
    reader.onload = async () => {
      const base64Url = reader.result as string;
      const newAttachment = {
        name: file.name,
        type: file.type.includes('pdf') ? 'pdf' : 'image',
        size: `${Math.round(file.size / 1024)} KB`,
        url: base64Url,
        uploadedAt: Timestamp.now(),
        uploadedBy: profile?.displayName || profile?.email || 'Technician'
      };

      try {
        const taskRef = doc(db, 'tasks', taskId);
        await updateDoc(taskRef, {
          attachments: [...(currentAttachments || []), newAttachment]
        });
        toast.success(`Attachment "${file.name}" uploaded successfully!`);
      } catch (err) {
        console.error('Error saving attachment:', err);
        toast.error('Failed to attach file to task');
      }
    };
    reader.onerror = () => {
      toast.error('Failed to read file');
    };
    reader.readAsDataURL(file);
  };

  const handleAttachmentDelete = async (taskId: string, indexToDelete: number, currentAttachments: any[]) => {
    try {
      const taskRef = doc(db, 'tasks', taskId);
      const updated = (currentAttachments || []).filter((_, idx) => idx !== indexToDelete);
      await updateDoc(taskRef, {
        attachments: updated
      });
      toast.success('Attachment removed');
    } catch (err) {
      console.error('Error deleting attachment:', err);
      toast.error('Failed to remove attachment');
    }
  };

  // Form state
  const [formData, setFormData] = useState({
    clientId: '',
    branchId: '',
    title: '',
    description: '',
    status: 'pending' as TaskStatus,
    technicianId: '',
    dueDate: new Date().toISOString().split('T')[0]
  });

  const [reportFormData, setReportFormData] = useState({
    equipment: [] as { name: string, model: string, serialNumber: string, status: string }[],
    findings: '',
    recommendations: '',
    comments: '',
    type: 'general' as ReportType,
    status: 'submitted_to_technical' as ReportStatus,
  });

  useEffect(() => {
    if (formData.clientId) {
      setIsBranchLoading(true);
      const branchesRef = collection(db, 'clients', formData.clientId, 'branches');
      const unsubscribe = onSnapshot(branchesRef, (snapshot) => {
        const branchesData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Branch));
        setClientBranches(branchesData);
        setIsBranchLoading(false);
      }, (error) => {
        handleFirestoreError(error, OperationType.LIST, `clients/${formData.clientId}/branches`);
        setIsBranchLoading(false);
      });
      return () => unsubscribe();
    } else {
      setClientBranches([]);
    }
  }, [formData.clientId]);

  useEffect(() => {
    // Fetch tasks
    const q = query(collection(db, 'tasks'), orderBy('createdAt', 'desc'));
    const unsubscribeTasks = onSnapshot(q, (snapshot) => {
      const tasksData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Task));
      setTasks(tasksData);
      setLoading(false);
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'tasks');
    });

    // Fetch clients
    const unsubscribeClients = onSnapshot(collection(db, 'clients'), (snapshot) => {
      const clientsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Client));
      setClients(clientsData);
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'clients');
    });

    // Fetch technicians
    const qTech = query(collection(db, 'users'), where('role', 'in', ['technician', 'manager', 'admin']));
    const unsubscribeTech = onSnapshot(qTech, (snapshot) => {
      const techData = snapshot.docs.map(doc => doc.data() as UserProfile);
      setTechnicians(techData);
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'users');
    });

    return () => {
      unsubscribeTasks();
      unsubscribeClients();
      unsubscribeTech();
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingTask) {
      setShowConfirmCreate(true);
      return;
    }
    await processSubmit();
  };

  const processSubmit = async () => {
    setIsSubmitting(true);
    const client = clients.find(c => c.id === formData.clientId);
    const technician = technicians.find(t => t.uid === formData.technicianId);
    const branch = clientBranches.find(b => b.id === formData.branchId);

    try {
      const taskData = {
        ...formData,
        clientName: client?.name || '',
        branchName: branch?.name || '',
        technicianName: technician?.displayName || '',
        dueDate: formData.dueDate ? Timestamp.fromDate(new Date(formData.dueDate)) : null,
        updatedAt: Timestamp.now()
      };

      if (editingTask) {
        await updateDoc(doc(db, 'tasks', editingTask.id), taskData);
        
        // Notify technician if task is completed
        if (taskData.status === 'completed' && editingTask.status !== 'completed') {
          if (taskData.technicianId) {
            await createNotification(
              taskData.technicianId,
              'Task Completed',
              `Task "${taskData.title}" has been marked as completed.`,
              'task_completed',
              '/tasks'
            );
          }
        }

        if (formData.technicianId && formData.technicianId !== editingTask.technicianId) {
          await createNotification(
            formData.technicianId,
            'New Task Assigned',
            `You have been assigned to: ${formData.title}`,
            'task_assigned',
            '/tasks'
          );
        }
        toast.success('Task updated successfully!');
      } else {
        await addDoc(collection(db, 'tasks'), {
          ...taskData,
          createdAt: Timestamp.now()
        });
        if (formData.technicianId) {
          await createNotification(
            formData.technicianId,
            'New Task Assigned',
            `You have been assigned to: ${formData.title}`,
            'task_assigned',
            '/tasks'
          );
        }
        toast.success('Task created successfully!');
      }
      closeModal();
      setShowConfirmCreate(false);
    } catch (error) {
      handleFirestoreError(error, editingTask ? OperationType.UPDATE : OperationType.CREATE, 'tasks');
      toast.error('Failed to save task.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStatusChange = async (taskId: string, newStatus: TaskStatus) => {
    try {
      const task = tasks.find(t => t.id === taskId);
      const historyEntry = {
        status: newStatus,
        changedBy: profile?.uid || 'unknown',
        changedByName: profile?.displayName || 'Unknown',
        changedAt: Timestamp.now()
      };
      
      await updateDoc(doc(db, 'tasks', taskId), {
        status: newStatus,
        updatedAt: Timestamp.now(),
        history: [...(task?.history || []), historyEntry]
      });
      toast.success('Status updated!');
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, `tasks/${taskId}`);
      toast.error('Failed to update status.');
    }
  };

  const handleDelete = async (taskId: string) => {
    setConfirmModal({
      isOpen: true,
      title: 'Delete Task',
      message: 'Are you sure you want to delete this task? This action cannot be undone.',
      onConfirm: async () => {
        try {
          await deleteDoc(doc(db, 'tasks', taskId));
          toast.success('Task deleted successfully!');
        } catch (error) {
          handleFirestoreError(error, OperationType.DELETE, `tasks/${taskId}`);
          toast.error('Failed to delete task.');
        }
      }
    });
  };

  const handlePrint = (task: Task) => {
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head><title>Task Report - ${task.title}</title></head>
          <body>
            <h1>Task: ${task.title}</h1>
            <p><strong>Client:</strong> ${task.clientName}</p>
            <p><strong>Status:</strong> ${task.status}</p>
            <p><strong>Description:</strong> ${task.description}</p>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
  };

  const openModal = (task?: Task) => {
    if (task) {
      setEditingTask(task);
      setFormData({
        clientId: task.clientId,
        branchId: task.branchId || '',
        title: task.title,
        description: task.description || '',
        status: task.status,
        technicianId: task.technicianId || '',
        dueDate: task.dueDate && typeof task.dueDate.toDate === 'function' ? task.dueDate.toDate().toISOString().split('T')[0] : new Date().toISOString().split('T')[0]
      });
    } else {
      setEditingTask(null);
      setFormData({
        clientId: filterClientId !== 'all' ? filterClientId : '',
        branchId: '',
        title: '',
        description: '',
        status: 'pending',
        technicianId: profile?.uid || '',
        dueDate: new Date().toISOString().split('T')[0]
      });
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingTask(null);
  };

  const openReportModal = (task: Task) => {
    setSelectedTaskForReport(task);
    setReportFormData({
      equipment: [],
      findings: '',
      recommendations: '',
      comments: '',
      type: 'general',
      status: 'submitted_to_technical',
    });
    setIsReportModalOpen(true);
  };

  const handleReportSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTaskForReport) return;

    setIsSubmitting(true);
    try {
      const reportData = {
        ...reportFormData,
        taskId: selectedTaskForReport.id,
        clientId: selectedTaskForReport.clientId,
        clientName: selectedTaskForReport.clientName || '',
        branchId: selectedTaskForReport.branchId || '',
        branchName: selectedTaskForReport.branchName || '',
        technicianId: profile?.uid || '',
        technicianName: profile?.displayName || '',
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      };

      const reportRef = await addDoc(collection(db, 'reports'), reportData);

      // Update task status
      await updateDoc(doc(db, 'tasks', selectedTaskForReport.id), {
        status: 'completed',
        updatedAt: Timestamp.now()
      });

      // Create notification for managers
      const qManagers = query(collection(db, 'users'), where('role', 'in', ['admin', 'manager']));
      const managersSnapshot = await getDocs(qManagers);
      for (const managerDoc of managersSnapshot.docs) {
        await createNotification(
          managerDoc.id,
          'New Report Submitted',
          `${profile?.displayName} submitted a report for task: ${selectedTaskForReport.title}`,
          'report_submitted',
          '/reports'
        );
      }

      // Notify technician about completion (even if they submitted it)
      if (selectedTaskForReport.technicianId) {
        await createNotification(
          selectedTaskForReport.technicianId,
          'Task Completed',
          `Task "${selectedTaskForReport.title}" has been marked as completed via report.`,
          'task_completed',
          '/tasks'
        );
      }

      toast.success('Report submitted and task marked as completed!');
      setIsReportModalOpen(false);
      setSelectedTaskForReport(null);
    } catch (error) {
      handleFirestoreError(error, OperationType.CREATE, 'reports');
      toast.error('Failed to submit report.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const addEquipmentRow = () => {
    setReportFormData({
      ...reportFormData,
      equipment: [...reportFormData.equipment, { name: '', model: '', serialNumber: '', status: '' }]
    });
  };

  const removeEquipmentRow = (index: number) => {
    const newEquipment = [...reportFormData.equipment];
    newEquipment.splice(index, 1);
    setReportFormData({ ...reportFormData, equipment: newEquipment });
  };

  const updateEquipmentRow = (index: number, field: string, value: string) => {
    const newEquipment = [...reportFormData.equipment];
    newEquipment[index] = { ...newEquipment[index], [field]: value };
    setReportFormData({ ...reportFormData, equipment: newEquipment });
  };

  const filteredTasks = tasks.filter(task => {
    const matchesSearch = task.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         task.clientName?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || task.status === filterStatus;
    const matchesClient = filterClientId === 'all' || task.clientId === filterClientId;
    
    let matchesDate = true;
    if (task.dueDate && typeof task.dueDate.toDate === 'function') {
      const taskDate = task.dueDate.toDate();
      taskDate.setHours(0, 0, 0, 0);
      
      if (filterDateStart) {
        const start = new Date(filterDateStart);
        start.setHours(0, 0, 0, 0);
        if (taskDate < start) matchesDate = false;
      }
      if (filterDateEnd) {
        const end = new Date(filterDateEnd);
        end.setHours(23, 59, 59, 999);
        if (taskDate > end) matchesDate = false;
      }
    } else if (filterDateStart || filterDateEnd) {
      // If filtering by date but task has no due date, hide it
      matchesDate = false;
    }
    
    return matchesSearch && matchesStatus && matchesClient && matchesDate;
  }).sort((a, b) => {
    const dateA = a.createdAt.toMillis();
    const dateB = b.createdAt.toMillis();
    return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
  });

  const getStatusIcon = (status: TaskStatus) => {
    switch (status) {
      case 'pending': return <Clock className="w-4 h-4" />;
      case 'in-progress': return <AlertCircle className="w-4 h-4" />;
      case 'completed': return <CheckCircle2 className="w-4 h-4" />;
    }
  };

  const getStatusColor = (status: TaskStatus) => {
    switch (status) {
      case 'pending': return 'bg-slate-100 text-slate-600 border-slate-200';
      case 'in-progress': return 'bg-blue-50 text-blue-800 border-blue-100';
      case 'completed': return 'bg-orange-50 text-orange-600 border-orange-100';
    }
  };

  const CalendarView = () => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(monthStart);
    const startDate = startOfWeek(monthStart);
    const endDate = endOfWeek(monthEnd);
    const calendarDays = eachDayOfInterval({ start: startDate, end: endDate });

    const getTasksForDay = (day: Date) => {
      return filteredTasks.filter(task => {
        const taskDate = (task.dueDate && typeof task.dueDate.toDate === 'function') 
          ? task.dueDate.toDate() 
          : (task.createdAt && typeof task.createdAt.toDate === 'function' ? task.createdAt.toDate() : new Date());
        return isSameDay(taskDate, day);
      });
    };

    return (
      <div className="bg-white rounded-[2rem] border border-slate-100 shadow-xl overflow-hidden">
        <div className="p-6 border-b border-slate-100 flex items-center justify-between">
          <h2 className="text-xl font-bold text-slate-900">{format(currentMonth, 'MMMM yyyy')}</h2>
          <div className="flex gap-2">
            <button onClick={() => setCurrentMonth(subMonths(currentMonth, 1))} className="p-2 hover:bg-slate-50 rounded-xl transition-colors">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button onClick={() => setCurrentMonth(new Date())} className="px-4 py-2 text-sm font-bold text-blue-800 hover:bg-blue-50 rounded-xl transition-colors">
              Today
            </button>
            <button onClick={() => setCurrentMonth(addMonths(currentMonth, 1))} className="p-2 hover:bg-slate-50 rounded-xl transition-colors">
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
        <div className="grid grid-cols-7 bg-slate-50/50 border-b border-slate-100">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} className="py-3 text-center text-xs font-bold text-slate-400 uppercase tracking-widest">
              {day}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {calendarDays.map((day, i) => {
            const dayTasks = getTasksForDay(day);
            return (
              <div 
                key={day.toISOString()} 
                className={cn(
                  "min-h-[120px] p-2 border-b border-r border-slate-100 transition-colors",
                  !isSameMonth(day, monthStart) && "bg-slate-50/30",
                  isToday(day) && "bg-blue-50/30"
                )}
              >
                <div className="flex justify-between items-start mb-2">
                  <span className={cn(
                    "text-sm font-bold w-7 h-7 flex items-center justify-center rounded-full",
                    isToday(day) ? "bg-blue-800 text-white" : "text-slate-600",
                    !isSameMonth(day, monthStart) && "text-slate-300"
                  )}>
                    {format(day, 'd')}
                  </span>
                </div>
                <div className="space-y-1">
                  {dayTasks.slice(0, 3).map(task => (
                    <div 
                      key={task.id}
                      onClick={() => openModal(task)}
                      className={cn(
                        "px-2 py-1 text-[10px] font-bold rounded-lg truncate cursor-pointer transition-transform hover:scale-105",
                        getStatusColor(task.status)
                      )}
                    >
                      {task.title}
                    </div>
                  ))}
                  {dayTasks.length > 3 && (
                    <div className="text-[10px] font-bold text-slate-400 pl-2">
                      + {dayTasks.length - 3} more
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  if (loading) return <div className="flex items-center justify-center h-64">Loading tasks...</div>;

  return (
    <div className="space-y-8">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Technical Work Progress</h1>
          <p className="text-slate-500">Track and manage technical operations in real-time</p>
        </div>
        {(isAdmin || isManager) && (
          <button
            onClick={() => openModal()}
            className="flex items-center justify-center gap-2 bg-blue-800 text-white px-6 py-3 rounded-2xl font-bold hover:bg-blue-900 transition-all shadow-lg shadow-blue-200 cursor-pointer"
          >
            <Plus className="w-5 h-5" />
            New Task
          </button>
        )}
      </div>

      <div className="flex items-center gap-4">
        <div className="flex bg-slate-100 p-1 rounded-2xl border border-slate-200">
          <button
            onClick={() => setViewMode('list')}
            className={cn(
              "flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold transition-all",
              viewMode === 'list' ? "bg-white text-blue-800 shadow-sm" : "text-slate-500 hover:text-slate-700"
            )}
          >
            <LayoutGrid className="w-4 h-4" />
            List
          </button>
          <button
            onClick={() => setViewMode('calendar')}
            className={cn(
              "flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold transition-all",
              viewMode === 'calendar' ? "bg-white text-blue-800 shadow-sm" : "text-slate-500 hover:text-slate-700"
            )}
          >
            <CalendarIcon className="w-4 h-4" />
            Calendar
          </button>
        </div>
      </div>

      {viewMode === 'list' ? (
        <>
          <FilterBar
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            filters={{ 
              status: filterStatus, 
              clientId: filterClientId,
              dueDateStart: filterDateStart,
              dueDateEnd: filterDateEnd
            }}
            onFilterChange={handleFilterChange}
            filterOptions={filterOptions}
          />

        <div className="grid grid-cols-1 gap-4">
          {filteredTasks.map((task) => {
            const client = clients.find(c => c.id === task.clientId);
            const isExpanded = expandedTaskId === task.id;
            return (
              <motion.div
                key={task.id}
                layout
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                className="group bg-white border border-slate-100 rounded-2xl p-5 hover:border-slate-200 hover:shadow-lg hover:shadow-slate-100 transition-all flex flex-col gap-4"
              >
                <div className="flex flex-col md:flex-row md:items-center gap-6">
                    <div 
                      className="flex-1 min-w-0 cursor-pointer"
                      onClick={() => setExpandedTaskId(isExpanded ? null : task.id)}
                    >
                      <div className="flex items-center gap-3 mb-2">
                        <span className={cn(
                          "flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border",
                          getStatusColor(task.status)
                        )}>
                          {getStatusIcon(task.status)}
                          {task.status.replace('-', ' ')}
                        </span>
                        <span className="text-xs text-slate-400 flex items-center gap-1">
                          <Building2 className="w-3 h-3" />
                          {task.clientName}
                          {task.branchName && ` - ${task.branchName}`}
                        </span>
                        {task.dueDate && (
                          <span className="text-xs text-blue-600 font-bold flex items-center gap-1">
                            <CalendarIcon className="w-3 h-3" />
                            Due: {task.dueDate?.toDate?.()?.toLocaleDateString() || 'N/A'}
                          </span>
                        )}
                      </div>
                      <h3 className="text-lg font-bold text-slate-900 truncate">{task.title}</h3>
                      {!isExpanded && task.description && (
                        <p className="text-sm text-slate-500 line-clamp-1 mt-1">{task.description}</p>
                      )}
                    </div>

                  <div className="flex items-center gap-4">
                    <button onClick={() => setExpandedTaskId(isExpanded ? null : task.id)} className="p-2 text-slate-400 hover:text-slate-900 hover:bg-slate-50 rounded-lg transition-all cursor-pointer">
                      {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                    </button>
                    
                    <div className="flex gap-1">
                      {isTechnician && task.technicianId === profile?.uid && task.status !== 'completed' && (
                        <button 
                          onClick={() => openReportModal(task)} 
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-all cursor-pointer" 
                          title="Submit Report"
                        >
                          <FileText className="w-4 h-4" />
                        </button>
                      )}
                      <button onClick={() => handlePrint(task)} className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all cursor-pointer" title="Print">
                        <Printer className="w-4 h-4" />
                      </button>
                      {task.status === 'completed' && (
                        <button onClick={() => setSelectedTaskForFeedback(task)} className="p-2 text-slate-400 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-all cursor-pointer" title="Feedback">
                          <Star className="w-4 h-4" />
                        </button>
                      )}
                      <button onClick={() => openModal(task)} className="p-2 text-slate-400 hover:text-slate-900 hover:bg-slate-50 rounded-lg transition-all cursor-pointer" title="Edit">
                        <Edit2 className="w-4 h-4" />
                      </button>
                      {(isAdmin || isManager) && (
                        <button onClick={() => handleDelete(task.id)} className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all cursor-pointer" title="Delete">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="border-t border-slate-100 pt-4 space-y-2"
                    >
                      <p className="text-sm text-slate-600">{task.description || 'No description provided'}</p>
                      
                      <div className="flex flex-col gap-4 mt-4">
                        <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-2xl border border-slate-100 w-fit">
                          {technicians.find(t => t.uid === task.technicianId)?.photoURL ? (
                            <img 
                              src={technicians.find(t => t.uid === task.technicianId)?.photoURL} 
                              alt={task.technicianName}
                              className="w-10 h-10 rounded-full object-cover border-2 border-white shadow-sm"
                              referrerPolicy="no-referrer"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-800 font-bold">
                              {task.technicianName?.charAt(0) || 'T'}
                            </div>
                          )}
                          <div>
                            <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Assigned Technician</p>
                            <p className="text-sm font-bold text-slate-900">{task.technicianName || 'Unassigned'}</p>
                          </div>
                        </div>

                        {client && (
                          <div className="flex flex-wrap gap-4 text-xs text-slate-500">
                            <div className="flex items-center gap-1">
                              <UserIcon className="w-3 h-3" />
                              <span>Contact: {client.contactPerson || 'N/A'}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              <span>Phone: {client.phone || 'N/A'}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              <span>Email: {client.email || 'N/A'}</span>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Attachments & Documentation Section */}
                      <div className="mt-6 pt-4 border-t border-slate-100 space-y-3">
                        <div className="flex items-center justify-between">
                          <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest flex items-center gap-1.5">
                            <Paperclip className="w-3.5 h-3.5" />
                            <span>Serviced Equipment Photos & PDF Manuals</span>
                          </h4>
                          <label className="text-[10px] font-black text-blue-900 bg-blue-50 border border-blue-100 px-2.5 py-1 rounded-xl cursor-pointer hover:bg-blue-100 transition-all flex items-center gap-1 shadow-sm">
                            <Upload className="w-3 h-3" />
                            <span>Add Attachment</span>
                            <input
                              type="file"
                              accept="image/*,application/pdf"
                              onChange={(e) => handleAttachmentUpload(task.id, e, task.attachments || [])}
                              className="hidden"
                            />
                          </label>
                        </div>

                        {(!task.attachments || task.attachments.length === 0) ? (
                          <p className="text-[10px] text-slate-400 font-medium italic">No documentation or equipment photos attached yet.</p>
                        ) : (
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {task.attachments.map((attachment, idx) => (
                              <div key={`${attachment.name}-${attachment.uploadedAt?.seconds || idx}`} className="flex items-center justify-between p-2.5 bg-slate-50 border border-slate-100 rounded-xl group hover:border-slate-200 transition-all">
                                <div className="flex items-center gap-2 overflow-hidden flex-1">
                                  {attachment.type === 'image' ? (
                                    <div className="relative w-8 h-8 rounded-lg overflow-hidden bg-slate-200 flex-shrink-0">
                                      <img src={attachment.url} alt={attachment.name} className="w-full h-full object-cover" />
                                    </div>
                                  ) : (
                                    <div className="w-8 h-8 rounded-lg bg-red-50 text-red-600 flex items-center justify-center flex-shrink-0 font-black text-[9px]">
                                      PDF
                                    </div>
                                  )}
                                  <div className="text-[11px] overflow-hidden text-ellipsis whitespace-nowrap flex-1">
                                    <a
                                      href={attachment.url}
                                      download={attachment.name}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="font-bold text-slate-800 hover:text-blue-900 hover:underline block truncate"
                                      title="Download attachment"
                                    >
                                      {attachment.name}
                                    </a>
                                    <span className="text-[9px] text-slate-400 block font-medium">Uploaded: {attachment.size || 'N/A'}</span>
                                  </div>
                                </div>
                                <button
                                  onClick={() => handleAttachmentDelete(task.id, idx, task.attachments || [])}
                                  className="p-1.5 text-slate-300 hover:text-red-600 rounded-lg transition-all opacity-0 group-hover:opacity-100 focus:opacity-100 cursor-pointer ml-1"
                                  title="Delete attachment"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {task.history && task.history.length > 0 && (
                        <div className="mt-4 pt-2 border-t border-slate-100">
                          <h4 className="text-xs font-bold text-slate-900 mb-2">Status History</h4>
                          <div className="space-y-1">
                            {task.history.map((h, i) => (
                              <div key={`${h.status}-${h.changedAt?.seconds || 0}-${i}`} className="text-[10px] text-slate-500 flex justify-between">
                                <span>{h.status} by {h.changedByName}</span>
                                <span>{h.changedAt?.toDate?.()?.toLocaleString() || 'N/A'}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </>
      ) : (
        <CalendarView />
      )}

      {/* Modal */}
      {/* Confirmation Modal for Task Creation */}
      <AnimatePresence>
        {showConfirmCreate && (
          <div className="fixed inset-0 z-[110] flex items-center justify-center p-6">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowConfirmCreate(false)}
              className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="relative w-full max-w-md bg-white rounded-[2.5rem] shadow-2xl p-8 text-center"
            >
              <div className="w-20 h-20 bg-blue-50 rounded-full flex items-center justify-center text-blue-800 mx-auto mb-6">
                <AlertCircle className="w-10 h-10" />
              </div>
              <h2 className="text-2xl font-black text-slate-900 tracking-tight mb-2">Confirm Task Creation</h2>
              <p className="text-slate-500 font-medium mb-8">Are you sure you want to create this task and assign it to the technician?</p>
              
              <div className="flex flex-col gap-3">
                <button
                  onClick={() => processSubmit()}
                  disabled={isSubmitting}
                  className="w-full py-4 bg-blue-800 text-white rounded-2xl font-bold hover:bg-blue-900 transition-all shadow-lg shadow-blue-200 flex items-center justify-center gap-2"
                >
                  {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Yes, Create Task'}
                </button>
                <button
                  onClick={() => setShowConfirmCreate(false)}
                  className="w-full py-4 bg-slate-100 text-slate-600 rounded-2xl font-bold hover:bg-slate-200 transition-all"
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isModalOpen && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-6">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeModal}
              className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="relative w-full max-w-2xl bg-white rounded-3xl shadow-2xl p-6 md:p-10 overflow-y-auto max-h-[90vh] mx-auto"
            >
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-2xl font-bold text-slate-900">
                  {editingTask ? 'Edit Task' : 'Create New Task'}
                </h2>
                <button onClick={closeModal} className="p-2 hover:bg-slate-100 rounded-xl transition-colors cursor-pointer">
                  <X className="w-6 h-6 text-slate-400" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-700">Client *</label>
                  <select
                    required
                    value={formData.clientId}
                    onChange={(e) => setFormData({ ...formData, clientId: e.target.value, branchId: '' })}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-slate-900/10 transition-all"
                  >
                    <option value="">Select a client</option>
                    {clients.map(client => (
                      <option key={client.id} value={client.id}>{client.name}</option>
                    ))}
                  </select>
                </div>

                {formData.clientId && clientBranches.length > 0 && (
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-slate-700">Branch *</label>
                    <select
                      required
                      value={formData.branchId}
                      onChange={(e) => setFormData({ ...formData, branchId: e.target.value })}
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-slate-900/10 transition-all"
                    >
                      <option value="">Select a branch</option>
                      {clientBranches.map(branch => (
                        <option key={branch.id} value={branch.id}>{branch.name}</option>
                      ))}
                    </select>
                  </div>
                )}

                {(formData.clientId && (clientBranches.length === 0 || formData.branchId)) && (
                  <>
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-slate-700">Task Title *</label>
                      <input
                        required
                        type="text"
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-slate-900/10 transition-all"
                        placeholder="e.g., Server Maintenance"
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-slate-700">Description</label>
                      <textarea
                        rows={3}
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-slate-900/10 transition-all resize-none"
                        placeholder="Describe the technical work to be done..."
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label className="text-sm font-semibold text-slate-700">Assigned Technician</label>
                        <select
                          value={formData.technicianId}
                          onChange={(e) => setFormData({ ...formData, technicianId: e.target.value })}
                          className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-slate-900/10 transition-all"
                        >
                          <option value="">Select technician</option>
                          {technicians.map(tech => (
                            <option key={tech.uid} value={tech.uid}>{tech.displayName || tech.email}</option>
                          ))}
                        </select>
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-semibold text-slate-700">Due Date</label>
                        <input
                          type="date"
                          value={formData.dueDate}
                          onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                          className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-slate-900/10 transition-all"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-semibold text-slate-700">Status</label>
                        <select
                          value={formData.status}
                          onChange={(e) => setFormData({ ...formData, status: e.target.value as TaskStatus })}
                          className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-slate-900/10 transition-all"
                        >
                          <option value="pending">Pending</option>
                          <option value="in-progress">In Progress</option>
                          <option value="completed">Completed</option>
                        </select>
                      </div>
                    </div>
                  </>
                )}

                <div className="flex flex-col sm:flex-row gap-4 pt-4">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="flex-1 px-6 py-3 border border-slate-200 rounded-2xl font-semibold text-slate-600 hover:bg-slate-50 transition-all cursor-pointer"
                  >
                    Close
                  </button>
                  <button
                    type="submit"
                    disabled={!!(isSubmitting || (formData.clientId && clientBranches.length > 0 && !formData.branchId))}
                    className="flex-1 px-6 py-3 bg-blue-800 text-white rounded-2xl font-bold hover:bg-blue-900 transition-all shadow-lg shadow-blue-200 cursor-pointer disabled:opacity-50"
                  >
                    {isSubmitting ? 'Saving...' : (editingTask ? 'Save Changes' : 'Create Task')}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
      {/* Report Modal */}
      <AnimatePresence>
        {isReportModalOpen && selectedTaskForReport && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-6">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsReportModalOpen(false)}
              className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="relative w-full max-w-3xl bg-white rounded-3xl shadow-2xl p-6 md:p-8 overflow-y-auto max-h-[90vh] mx-auto"
            >
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="text-2xl font-bold text-slate-900">Submit Technical Report</h2>
                  <p className="text-sm text-slate-500">{selectedTaskForReport.title}</p>
                </div>
                <button onClick={() => setIsReportModalOpen(false)} className="p-2 hover:bg-slate-100 rounded-xl transition-colors">
                  <X className="w-6 h-6 text-slate-400" />
                </button>
              </div>

              <form onSubmit={handleReportSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-slate-700">Report Type</label>
                    <select
                      value={reportFormData.type}
                      onChange={(e) => setReportFormData({ ...reportFormData, type: e.target.value as ReportType })}
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-slate-900/10 transition-all"
                    >
                      <option value="general">General Report</option>
                      <option value="inspection">Inspection Report</option>
                      <option value="survey">Survey Report</option>
                    </select>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-semibold text-slate-700">Equipment Details</label>
                    <button
                      type="button"
                      onClick={addEquipmentRow}
                      className="text-xs font-bold text-blue-800 hover:text-blue-900 flex items-center gap-1"
                    >
                      <Plus className="w-3 h-3" /> Add Equipment
                    </button>
                  </div>
                  <div className="space-y-3">
                    {reportFormData.equipment.map((eq, idx) => (
                      <div key={`${idx}-${eq.name}`} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 p-4 bg-slate-50 rounded-2xl border border-slate-100 relative">
                        <input
                          placeholder="Name"
                          value={eq.name}
                          onChange={(e) => updateEquipmentRow(idx, 'name', e.target.value)}
                          className="px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm"
                        />
                        <input
                          placeholder="Model"
                          value={eq.model}
                          onChange={(e) => updateEquipmentRow(idx, 'model', e.target.value)}
                          className="px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm"
                        />
                        <input
                          placeholder="Serial No"
                          value={eq.serialNumber}
                          onChange={(e) => updateEquipmentRow(idx, 'serialNumber', e.target.value)}
                          className="px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm"
                        />
                        <div className="flex gap-2">
                          <input
                            placeholder="Status"
                            value={eq.status}
                            onChange={(e) => updateEquipmentRow(idx, 'status', e.target.value)}
                            className="flex-1 px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm"
                          />
                          <button
                            type="button"
                            onClick={() => removeEquipmentRow(idx)}
                            className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-700">Findings *</label>
                  <textarea
                    required
                    rows={3}
                    value={reportFormData.findings}
                    onChange={(e) => setReportFormData({ ...reportFormData, findings: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-slate-900/10 transition-all resize-none"
                    placeholder="What did you find during the work?"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-700">Recommendations</label>
                  <textarea
                    rows={3}
                    value={reportFormData.recommendations}
                    onChange={(e) => setReportFormData({ ...reportFormData, recommendations: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-slate-900/10 transition-all resize-none"
                    placeholder="Any recommendations for the client?"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-700">Comments</label>
                  <textarea
                    rows={2}
                    value={reportFormData.comments}
                    onChange={(e) => setReportFormData({ ...reportFormData, comments: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-slate-900/10 transition-all resize-none"
                    placeholder="Additional comments..."
                  />
                </div>

                <div className="flex flex-col sm:flex-row gap-4 pt-4">
                  <button
                    type="button"
                    onClick={() => setIsReportModalOpen(false)}
                    className="flex-1 px-6 py-3 border border-slate-200 rounded-2xl font-semibold text-slate-600 hover:bg-slate-50 transition-all cursor-pointer"
                  >
                    Close
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex-1 px-6 py-3 bg-blue-800 text-white rounded-2xl font-bold hover:bg-blue-900 transition-all shadow-lg shadow-blue-200 cursor-pointer flex items-center justify-center gap-2"
                  >
                    {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Send className="w-4 h-4" /> Submit Report</>}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {selectedTaskForFeedback && (
        <FeedbackModal
          task={selectedTaskForFeedback}
          isOpen={!!selectedTaskForFeedback}
          onClose={() => setSelectedTaskForFeedback(null)}
        />
      )}
      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmLabel="YES"
        cancelLabel="NO"
        isDanger={true}
      />
    </div>
  );
};

export default TasksPage;
